/**
 * Tempi col cliente secondo la PRO 09 (QA26-F4.11): presa in carico, ultimo aggiornamento, prossimo dovuto, ritardo;
 * «Prendi in carico» e «Registra aggiornamento al cliente». E «Apri NC da questa segnalazione» (QA26-F4.10).
 */
import { App, Button, Card, DatePicker, Input, Modal, Radio, Space, Tag } from "antd";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import dayjs, { type Dayjs } from "dayjs";
import { TIPI_NC, type SegnalazioneDto, type StatoPro09, type TipoNc } from "@qa/shared";
import { ANCORA } from "@qa/shared";
import { api } from "../../../api/client";
import { fmtData, fmtDataOra } from "../../../lib/format";
import { usePuo } from "../../../lib/permessi";
import { useAggiorna } from "../../../lib/query";
import { messaggioErrore } from "../../../i18n/errori";
import { FornitoreSelect, SelectLista } from "../../../components/campi";
import { Miss } from "../../../components/ui";
import { palette } from "../../../theme";

/** Etichetta corta del prossimo aggiornamento (elenco, home). */
export function Pro09Tag({ p }: { p: StatoPro09 | null }) {
  const { t } = useTranslation();
  if (!p) return <span className="muted">—</span>;
  return p.inRitardo ? (
    <Tag color="red" bordered={false}>
      {fmtData(p.prossimo)} · {t("redflag.pro09.ritardoBreve")}
    </Tag>
  ) : (
    <span className="num">{fmtData(p.prossimo)}</span>
  );
}

function RegistraAggiornamento({ r, onClose }: { r: SegnalazioneDto; onClose: () => void }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const aggiorna = useAggiorna();
  const [testo, setTesto] = useState("");
  const [data, setData] = useState<Dayjs | null>(dayjs());
  const m = useMutation({
    mutationFn: () => api.post<SegnalazioneDto>(`/redflag/segnalazioni/${r.id}/aggiornamento-cliente`, { testo: testo.trim(), data: data?.format("YYYY-MM-DD") }),
    onSuccess: () => {
      message.success(t("redflag.pro09.registrato"));
      void aggiorna();
      onClose();
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  return (
    <Modal open onCancel={onClose} title={t("redflag.pro09.registra")} okText={t("azioni.salva")} cancelText={t("azioni.annulla")} okButtonProps={{ disabled: !testo.trim() }} confirmLoading={m.isPending} onOk={() => m.mutate()} destroyOnHidden>
      <div className="muted small">{t("redflag.pro09.registraTesto")}</div>
      <Input.TextArea rows={4} maxLength={4000} value={testo} onChange={(e) => setTesto(e.target.value)} style={{ margin: "4px 0 12px" }} />
      <div className="muted small">{t("redflag.pro09.registraData")}</div>
      <DatePicker value={data} onChange={setData} format="DD/MM/YYYY" disabledDate={(d) => d.isAfter(dayjs(), "day")} style={{ marginTop: 4 }} />
    </Modal>
  );
}

export function Pro09Card({ r }: { r: SegnalazioneDto }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const aggiorna = useAggiorna();
  const [reg, setReg] = useState(false);
  const p = r.pro09;
  const presa = useMutation({
    mutationFn: () => api.post<SegnalazioneDto>(`/redflag/segnalazioni/${r.id}/presa-in-carico`),
    onSuccess: () => {
      message.success(t("redflag.pro09.presaOk"));
      void aggiorna();
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  return (
    <Card size="small" title={t("redflag.pro09.titolo")} style={{ marginTop: 16, ...(p?.inRitardo ? { borderColor: palette.bad } : {}) }} data-tip={ANCORA.pro09}>
      <dl className="kv stretto">
        <dt>{t("redflag.pro09.ricevuta")}</dt>
        <dd className="num">{fmtData(r.ricevutaIl)}</dd>
        <dt>{t("redflag.pro09.presaInCarico")}</dt>
        <dd>{r.presaInCaricoIl ? `${fmtDataOra(r.presaInCaricoIl)}${r.presaInCaricoDa ? ` · ${r.presaInCaricoDa}` : ""}` : <Miss>{t("redflag.pro09.nonPresa")}</Miss>}</dd>
        <dt>{t("redflag.pro09.ultimo")}</dt>
        <dd className="num">{r.ultimoAggiornamentoCliente ? fmtData(r.ultimoAggiornamentoCliente) : <span className="muted">{t("redflag.pro09.nessuno")}</span>}</dd>
        {p && (
          <>
            <dt>{t("redflag.pro09.prossimo")}</dt>
            <dd>
              <b className="num" style={{ color: p.inRitardo ? palette.bad : undefined }}>
                {fmtData(p.prossimo)}
              </b>{" "}
              {p.inRitardo ? (
                <Tag color="red" bordered={false}>
                  {t("redflag.pro09.inRitardo", { n: p.giorniRitardo })}
                </Tag>
              ) : (
                <Tag color="green" bordered={false}>
                  {t("redflag.pro09.inTempo")}
                </Tag>
              )}
            </dd>
          </>
        )}
      </dl>
      <p className="muted small" style={{ margin: "8px 0" }}>
        {!p ? (r.chiusaIl ? t("redflag.pro09.chiusa", { data: fmtData(r.chiusaIl) }) : t("redflag.pro09.nonAperta")) : r.vip ? t("redflag.pro09.regolaVip") : t("redflag.pro09.regola")}
      </p>
      {r.puo.modificare && p && (
        <Space wrap>
          {!r.presaInCaricoIl && (
            <Button size="small" loading={presa.isPending} onClick={() => presa.mutate()}>
              {t("redflag.pro09.prendi")}
            </Button>
          )}
          <Button size="small" type={p.inRitardo ? "primary" : "default"} onClick={() => setReg(true)}>
            {t("redflag.pro09.registra")}
          </Button>
        </Space>
      )}
      {reg && <RegistraAggiornamento r={r} onClose={() => setReg(false)} />}
    </Card>
  );
}

export function ApriNcModal({ r, onClose }: { r: SegnalazioneDto; onClose: () => void }) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const puo = usePuo();
  const aggiorna = useAggiorna();
  const esistenti = new Set(r.nc.map((n) => n.tipo));
  const [tipo, setTipo] = useState<TipoNc>(TIPI_NC.find((x) => !esistenti.has(x)) ?? "fornitore");
  const iniziale = r.classificazione?.fornitore ?? r.classificazioneProposta?.fornitore ?? null;
  const [fornitoreId, setFornitoreId] = useState<string | null>(iniziale?.id ?? null);
  const [funzione, setFunzione] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () =>
      api.post<SegnalazioneDto>(`/redflag/segnalazioni/${r.id}/nc`, tipo === "fornitore" ? { tipo, fornitoreId, fornitoreTesto: fornitoreId ? null : (r.classificazione?.fornitoreTesto ?? null) } : { tipo, funzione }),
    onSuccess: () => {
      message.success(t("redflag.apriNc.aperta"));
      void aggiorna();
      onClose();
    },
    onError: (e) => message.error(messaggioErrore(e)),
  });
  return (
    <Modal open onCancel={onClose} title={t("redflag.apriNc.titolo")} okText={t("redflag.apriNc.apri")} cancelText={t("azioni.annulla")} okButtonProps={{ disabled: esistenti.has(tipo) }} confirmLoading={m.isPending} onOk={() => m.mutate()} destroyOnHidden>
      <p className="muted small" style={{ marginTop: 0 }}>
        {t("redflag.apriNc.nota")}
      </p>
      <div className="muted small">{t("redflag.apriNc.tipo")}</div>
      <Radio.Group
        optionType="button"
        value={tipo}
        onChange={(e) => setTipo(e.target.value as TipoNc)}
        options={TIPI_NC.map((x) => ({ value: x, label: t(`dominio.tipoNc.${x}`), disabled: esistenti.has(x) }))}
        style={{ margin: "4px 0 12px" }}
      />
      {tipo === "fornitore" ? (
        <>
          <div className="muted small">{t("redflag.apriNc.fornitore")}</div>
          {puo("fornitore.vedi") ? <FornitoreSelect value={fornitoreId} iniziale={iniziale} onChange={(id) => setFornitoreId(id)} /> : <span>{r.classificazione?.fornitoreTesto ?? "—"}</span>}
        </>
      ) : (
        <>
          <div className="muted small">{t("redflag.apriNc.funzione")}</div>
          <SelectLista lista="funzione_nc" value={funzione} onChange={setFunzione} />
        </>
      )}
    </Modal>
  );
}
